import React from 'react';
import { ConfirmModal } from '../../../components/modals/ConfirmModal';
import { CreateProjectModal } from './CreateProjectModal';
import { InviteMemberModal } from './InviteMemberModal';
import { ManageMemberProjectsModal } from './ManageMemberProjectsModal';
import { DeleteOrgModal } from './DeleteOrgModal';
import { SubscriptionModals } from './SubscriptionModals';
import { useOrganizationData } from '../useOrganizationData';

interface OrganizationModalsProps {
  data: ReturnType<typeof useOrganizationData>;
}

export const OrganizationModals: React.FC<OrganizationModalsProps> = ({ data }) => {
  return (
    <>
      <CreateProjectModal
        isOpen={data.createProjectOpen}
        onClose={() => data.setCreateProjectOpen(false)}
        onSubmit={data.handleCreateProject}
        projectName={data.newProjectName}
        onProjectNameChange={data.setNewProjectName}
        isCreating={data.isCreatingProject}
        orgName={data.currentOrg?.name}
      />

      <InviteMemberModal
        isOpen={data.inviteModalOpen}
        onClose={() => data.setInviteModalOpen(false)}
        onSubmit={data.handleSendInvite}
        inviteEmail={data.inviteEmail}
        onInviteEmailChange={data.setInviteEmail}
        inviteRole={data.inviteRole}
        onInviteRoleChange={data.setInviteRole}
        inviteProjects={data.inviteProjects}
        onInviteProjectsChange={data.setInviteProjects}
        projects={data.projects}
        isSending={data.isSendingInvite}
        generatedInviteUrl={data.generatedInviteUrl}
        onResetGeneratedUrl={() => {
          data.setGeneratedInviteUrl(null);
          data.setInviteEmail('');
        }}
        copiedLink={data.copiedLink}
        onCopyLink={data.handleCopyInviteLink}
      />

      <ManageMemberProjectsModal
        managingMember={data.managingMember}
        onClose={() => data.setManagingMember(null)}
        projects={data.projects}
        assignedProjects={data.memberAssignedProjects}
        onAssignedProjectsChange={data.setMemberAssignedProjects}
        isLoading={data.isLoadingMemberProjects}
        isSaving={data.isSavingMemberProjects}
        onSave={data.handleSaveMemberProjects}
      />

      <ConfirmModal
        isOpen={!!data.memberToRemove}
        onClose={() => data.setMemberToRemove(null)}
        onConfirm={data.handleExecuteRemoveMember}
        title="Remove Organization Member"
        message={`Are you sure you want to remove "${data.memberToRemove?.full_name || data.memberToRemove?.email || 'this member'}" from ${data.currentOrg?.name}? They will lose access to all projects and datastores within this organization immediately.`}
        confirmLabel={data.isRemovingMember ? 'Removing...' : 'Remove Member'}
        isLoading={data.isRemovingMember}
        isDestructive={true}
      />

      {data.canDeleteOrg && (
        <DeleteOrgModal
          isOpen={data.deleteOrgModalOpen}
          onClose={() => data.setDeleteOrgModalOpen(false)}
          currentOrg={data.currentOrg}
          confirmText={data.deleteOrgConfirmText}
          onConfirmTextChange={data.setDeleteOrgConfirmText}
          isDeleting={data.isDeletingOrg}
          onDelete={data.handleDeleteOrg}
        />
      )}

      <SubscriptionModals
        currentOrg={data.currentOrg}
        quota={data.quota}
        isMutating={data.isMutatingSubscription}
        upgradeTargetPlan={data.upgradeTargetPlan}
        onCloseUpgrade={() => data.setUpgradeTargetPlan(null)}
        onConfirmUpgrade={data.handleUpgrade}
        downgradeTargetPlan={data.downgradeTargetPlan}
        onCloseDowngrade={() => data.setDowngradeTargetPlan(null)}
        onConfirmDowngrade={data.handleDowngrade}
        cancelModalOpen={data.cancelModalOpen}
        onCloseCancel={() => data.setCancelModalOpen(false)}
        onConfirmCancel={data.handleCancelSubscription}
      />
    </>
  );
};
